import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  DialogName,
  Prefecture,
  Survey,
  Template,
  TokenInfo,
  Breadcrumb,
} from 'interface'
import { VariantType } from 'notistack'
import axiosClient from 'utils/axiosClient'

const initialState: {
  auth?: TokenInfo
  status: 'init' | 'loading' | 'fulfilled' | 'error'
  notice: string
  snackbarMessage?: {
    text: string
    type: VariantType
  }
  dialogName: DialogName
  prefectures: Prefecture[]
  templates: Template[]
  surveys: Survey[]
  breadcrumbs: Breadcrumb[]
} = {
  notice: '',
  dialogName: '',
  status: 'init',
  prefectures: [],
  templates: [],
  surveys: [],
  breadcrumbs: [],
}

export const getAuth = createAsyncThunk('UI/getAuth', async () => {
  const { data } = await axiosClient.get('/auth/organizer')
  return data
})
export const logout = createAsyncThunk('UI/logout', async () => {
  await axiosClient.post('/auth/logout')
})
export const getPrefectures = createAsyncThunk('UI/prefectures', async () => {
  const { data } = await axiosClient.get('/api/prefectures')
  return data
})
export const getTemplates = createAsyncThunk('UI/templates', async () => {
  const { data } = await axiosClient.get('/api/templates')
  return data
})
export const getSurveys = createAsyncThunk('UI/getSurveys', async () => {
  const { data } = await axiosClient.get('/api/events/surveys')
  return data
})
export const getEventSurveys = createAsyncThunk('UI/getEventSurveys', async (id: number) => {
  const { data } = await axiosClient.get(`/api/events/${id}/surveys`)
  return data
})

export const UISlice = createSlice({
  name: 'UI',
  initialState,
  reducers: {
    setAuth(state, { payload }: PayloadAction<TokenInfo>) {
      state.auth = payload
    },
    setSnackbarMessage(
      state,
      {
        payload,
      }: PayloadAction<
        | {
            text: string
            type: VariantType
          }
        | undefined
      >
    ) {
      state.snackbarMessage = payload
    },
    setNotice(state, { payload }: PayloadAction<string>) {
      state.notice = payload
    },
    setBreadcrumbs(state, { payload }: PayloadAction<Breadcrumb[]>) {
      state.breadcrumbs = [...payload]
    },
    setDialogName(state, { payload }: PayloadAction<DialogName>) {
      state.dialogName = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAuth.fulfilled, (state, { payload }) => {
      state.status = 'fulfilled'
      if (payload.id) {
        state.auth = payload
      }
    })
    builder.addCase(getAuth.rejected, (state, { payload }) => {
      state.status = 'error'
    })
    builder.addCase(logout.fulfilled, (state, { payload }) => {
      state.auth = undefined
    })
    builder.addCase(getPrefectures.fulfilled, (state, { payload }) => {
      state.prefectures = payload
    })
    builder.addCase(getTemplates.fulfilled, (state, { payload }) => {
      state.templates = payload
    })
    builder.addCase(getSurveys.fulfilled, (state, { payload }) => {
      state.surveys = payload
    })
    builder.addCase(getEventSurveys.fulfilled, (state, { payload }) => {
      state.surveys = payload
    })
  },
})

export const UIAction = UISlice.actions

export const UIReducer = UISlice.reducer
